import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { useLocation } from '../../../hooks/useLocation';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
const LocationSection = ({ taskForm, setTaskForm, validationErrors = {}, isRequired = false }) => {
  const {
    isLoadingLocation,
    locationError,
    locationSuggestions,
    showSuggestions,
    suggestionsRef,
    getCurrentLocationWithAddress,
    searchPlaces,
    selectSuggestion,
    setLocationError,
    isProcessingUrl,
    isGoogleMapsUrl,
    processGoogleMapsUrl
  } = useLocation();

  const [mapKey, setMapKey] = useState(0);
  const [urlDetected, setUrlDetected] = useState(false);

  useEffect(() => {
    if (taskForm.location?.coordinates?.lat && taskForm.location?.coordinates?.lng) {
      setMapKey(prev => prev + 1);
    }
  }, [taskForm.location?.coordinates]);

  const handleLocationInputChange = (e) => {
    const { value } = e.target;
    
    // Check if the input looks like a Google Maps URL
    setUrlDetected(isGoogleMapsUrl(value));
    
    setTaskForm(prev => ({
      ...prev,
      location: {
        ...prev.location,
        address: value
      }
    }));
    
    if (value && locationError) {
      setLocationError('');
    }
    
    searchPlaces(value);
  };

  const handleSelectSuggestion = (suggestion) => {
    const location = selectSuggestion(suggestion);
    setTaskForm(prev => ({
      ...prev,
      location
    }));
  };

  const handleGetLocation = async () => {
    const result = await getCurrentLocationWithAddress();
    
    if (result.success) {
      setTaskForm(prev => ({
        ...prev,
        location: result.location
      }));
      
      if (result.warning) {
        setLocationError(result.warning);
      }
    } else {
      setLocationError(result.error);
    }
  };

  const handleMarkerDrag = (event) => {
    const marker = event.target;
    const position = marker.getLatLng();
    
    setTaskForm(prev => ({
      ...prev,
      location: {
        ...prev.location,
        coordinates: {
          lat: position.lat,
          lng: position.lng
        }
      }
    }));
    
    // Reverse geocode to get the address
    reverseGeocode(position.lat, position.lng);
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.display_name) {
        setTaskForm(prev => ({
          ...prev,
          location: {
            ...prev.location,
            address: data.display_name
          }
        }));
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
      // Keep the coordinates but don't update the address if reverse geocoding fails
    }
  };

  // Handle paste events to detect Google Maps URLs
  const handlePaste = async (e) => {
    const pastedText = e.clipboardData.getData('text');
    
    // Check if the pasted text is a Google Maps URL
    if (isGoogleMapsUrl(pastedText)) {
      e.preventDefault(); // Prevent the default paste behavior
      
      // Process the Google Maps URL
      const result = await processGoogleMapsUrl(pastedText);
      
      if (result.success) {
        setTaskForm(prev => ({
          ...prev,
          location: result.location
        }));
        
        if (result.warning) {
          setLocationError(result.warning);
        }
      } else {
        setLocationError(result.error);
        // Still paste the URL so user can see what was pasted
        setTaskForm(prev => ({
          ...prev,
          location: {
            ...prev.location,
            address: pastedText
          }
        }));
      }
    }
    // If it's not a Google Maps URL, let the default paste behavior happen
  };

  // Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});
// Custom Marker Icons - Circular design like Tasks.js
const createCircularIcon = (color) => {
  
  return L.divIcon({
      className: `custom-marker`,
      html: `
          <div class="marker-dot" style="
              background-color: ${color};
              width: 24px;
              height: 24px;
              border-radius: 50%;
              border: 2px solid white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 12px;
              position: relative;
          ">
              <span style="transform: translateY(1px)">•</span>
          </div>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
  });
};
  return (
    <div className="form-group location-section">
      <label>Location{isRequired ? <span className="required-asterisk">*</span> : ' (Optional)'}</label>
      
      <div className="location-input-container">
        <div className="location-autocomplete-wrapper" ref={suggestionsRef}>
          <div className="input-with-url-indicator">
            <input
              type="text"
              className={`form-control${validationErrors.location ? ' error' : ''}`}
              value={taskForm.location?.address || ''}
              onChange={handleLocationInputChange}
              onPaste={handlePaste}
              placeholder="Enter location or paste Google Maps URL"
              style={{ color: '#222', background: '#fff' }}
              disabled={isProcessingUrl}
              required={isRequired}
            />
            {urlDetected && !isProcessingUrl && (
              <div className="url-indicator" title="Google Maps URL detected">
                🗺️
              </div>
            )}
            {isProcessingUrl && (
              <div className="url-indicator">
                <span className="location-spinner"></span>
              </div>
            )}
          </div>
          {showSuggestions && locationSuggestions.length > 0 && (
            <div className="location-suggestions">
              {locationSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleSelectSuggestion(suggestion)}
                >
                  {suggestion.display_name}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <button
          type="button"
          className="location-button"
          onClick={handleGetLocation}
          disabled={isLoadingLocation || isProcessingUrl}
        >
          {isLoadingLocation || isProcessingUrl ? (
            <span className="location-spinner"></span>
          ) : (
            'Get Current Location'
          )}
        </button>
      </div>

      {(isProcessingUrl) && (
        <div className="location-loading">
          <span className="location-spinner"></span>
          <span>Processing Google Maps URL...</span>
        </div>
      )}

      {(locationError || validationErrors.location) && (
        <div className="location-error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{validationErrors.location || locationError}</div>
        </div>
      )}

      <div 
        className="map-container task-modal-map" 
        style={{ 
          marginTop: 12, 
          border: '1px solid #ddd', 
          borderRadius: 4, 
          background: '#f8f9fa', 
          minHeight: 220 
        }}
      >
        {(taskForm.location?.coordinates?.lat && taskForm.location?.coordinates?.lng) ? (
          <MapContainer 
            key={mapKey}
            center={[taskForm.location.coordinates.lat, taskForm.location.coordinates.lng]}
            zoom={15}
            scrollWheelZoom={false}
            style={{ height: 220, width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={[taskForm.location.coordinates.lat, taskForm.location.coordinates.lng]}
            icon={createCircularIcon('#000')}
            draggable={true}
            eventHandlers={{
              dragend: handleMarkerDrag
            }}
>
              <Popup>Selected Location</Popup>
            </Marker>
          </MapContainer>
        ) : (
          <div style={{ 
            height: 220, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            color: '#888' 
          }}>
            No location selected
          </div>
        )}
      </div>

     {/*  {taskForm.location?.coordinates?.lat && taskForm.location?.coordinates?.lng && (
        <div className="coordinates-display" style={{ 
          marginTop: 4, 
          fontSize: 12, 
          color: '#666' 
        }}>
          Coordinates: {taskForm.location.coordinates.lat.toFixed(6)}, {taskForm.location.coordinates.lng.toFixed(6)}
        </div>
      )} */}
    </div>
  );
};

export default LocationSection; 